import { useRef, useState } from 'react';
import type { ListingAssistResultDto } from '../../types';
import {
    MAX_TAG_IMAGES,
    MAX_TAG_BYTES,
    compressImageForOcr,
    countAssistFields,
} from '../../utils/listingAssistMap';
import { analyzeListingTagImages } from '../../services/api.shop';
import { ValidationError } from '../../admin/components/ValidationError';
import { translateWithFallback } from '../../utils/translateWithFallback';

export type TagPreview = { id: string; file: File; preview: string };

type Props = {
    t: (key: string, params?: Record<string, unknown>) => string;
    disabled?: boolean;
    onApplyAll: (result: ListingAssistResultDto) => void;
};

function tr(
    t: Props['t'],
    key: string,
    fallback: string,
    params?: Record<string, unknown>
): string {
    return translateWithFallback(t, key, fallback, params);
}

/**
 * Optional inline assist: capture/upload care-label photos → OCR suggestions → Apply all.
 * Tag photos are never product listing images.
 */
const ListingAssistPanel = ({ t, disabled, onApplyAll }: Props) => {
    const fileRef = useRef<HTMLInputElement>(null);
    const cameraRef = useRef<HTMLInputElement>(null);
    const [tagImages, setTagImages] = useState<TagPreview[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<ListingAssistResultDto | null>(null);
    const [applied, setApplied] = useState(false);

    const addFiles = async (list: FileList | null) => {
        if (!list?.length) return;
        setError(null);
        setResult(null);
        setApplied(false);

        const incoming = Array.from(list);
        const room = MAX_TAG_IMAGES - tagImages.length;
        if (room <= 0) {
            setError(
                tr(t, 'form.productform.assist.errors.max', `Maximum ${MAX_TAG_IMAGES} tag photos.`, {
                    max: MAX_TAG_IMAGES,
                })
            );
            return;
        }

        const slice = incoming.slice(0, room);
        const next: TagPreview[] = [];

        for (const file of slice) {
            if (!['image/jpeg', 'image/png'].includes(file.type) && !/\.(jpe?g|png)$/i.test(file.name)) {
                setError(
                    tr(t, 'form.productform.assist.errors.type', 'Only JPEG and PNG tag photos are allowed.')
                );
                continue;
            }
            let prepared = file;
            try {
                prepared = await compressImageForOcr(file);
            } catch {
                prepared = file;
            }
            if (prepared.size > MAX_TAG_BYTES) {
                setError(
                    tr(
                        t,
                        'form.productform.assist.errors.size',
                        'Each tag photo must be under 5 MB. Try a closer crop.'
                    )
                );
                continue;
            }
            next.push({
                id: crypto.randomUUID(),
                file: prepared,
                preview: URL.createObjectURL(prepared),
            });
        }

        if (next.length) {
            setTagImages((prev) => [...prev, ...next]);
        }
        if (fileRef.current) fileRef.current.value = '';
        if (cameraRef.current) cameraRef.current.value = '';
    };

    const removeTag = (id: string) => {
        setTagImages((prev) => {
            const img = prev.find((x) => x.id === id);
            if (img) URL.revokeObjectURL(img.preview);
            return prev.filter((x) => x.id !== id);
        });
        setResult(null);
        setApplied(false);
        setError(null);
    };

    const clearAll = () => {
        tagImages.forEach((img) => URL.revokeObjectURL(img.preview));
        setTagImages([]);
        setResult(null);
        setApplied(false);
        setError(null);
    };

    const handleAnalyze = async () => {
        if (tagImages.length === 0) {
            setError(
                tr(t, 'form.productform.assist.errors.needPhoto', 'Add 1–3 photos of the product label first.')
            );
            return;
        }
        setLoading(true);
        setError(null);
        setApplied(false);
        try {
            const data = await analyzeListingTagImages(tagImages.map((x) => x.file));
            // Normalize casing from ASP.NET default camelCase
            const normalized: ListingAssistResultDto = {
                suggestedTitle: data.suggestedTitle ?? (data as { SuggestedTitle?: string }).SuggestedTitle,
                descriptionHint:
                    data.descriptionHint ?? (data as { DescriptionHint?: string }).DescriptionHint,
                fields: data.fields ?? (data as { Fields?: Record<string, string> }).Fields ?? {},
                rawLines: data.rawLines ?? (data as { RawLines?: string[] }).RawLines ?? [],
                source: data.source ?? 'ocr',
            };
            setResult(normalized);
            if (countAssistFields(normalized) === 0 && !(normalized.suggestedTitle ?? '').trim()) {
                setError(
                    tr(
                        t,
                        'form.productform.assist.errors.nothing',
                        'We could not read brand or size from these photos. Try a sharper close-up, or fill the form manually.'
                    )
                );
            }
        } catch (err: unknown) {
            const ax = err as {
                response?: { data?: { errors?: { tagImages?: string }; title?: string } };
            };
            const msg =
                ax.response?.data?.errors?.tagImages ||
                tr(
                    t,
                    'form.productform.assist.errors.failed',
                    'We could not read the label right now. Please try again or fill the form manually.'
                );
            setError(msg);
            setResult(null);
        } finally {
            setLoading(false);
        }
    };

    const handleApplyAll = () => {
        if (!result) return;
        onApplyAll(result);
        setApplied(true);
    };

    const fieldCount = countAssistFields(result);
    const hasSuggestion =
        !!result && (fieldCount > 0 || !!(result.suggestedTitle ?? '').trim());

    return (
        <section
            className="rounded-2xl border border-[color-mix(in_srgb,var(--accent-color),transparent_40%)] bg-[color-mix(in_srgb,var(--bg-color),white_40%)] p-4 sm:p-5 shadow-sm"
            aria-labelledby="listing-assist-heading"
        >
            <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                <div>
                    <h2
                        id="listing-assist-heading"
                        className="text-base sm:text-lg font-semibold text-[var(--text-color)]"
                    >
                        {tr(t, 'form.productform.assist.title', 'Scan product label (optional)')}
                    </h2>
                    <p className="mt-1 text-xs sm:text-sm text-[color-mix(in_srgb,var(--text-color),transparent_40%)]">
                        {tr(
                            t,
                            'form.productform.assist.hint',
                            'Take 1–3 close-ups of the care label or hangtag. We read brand, size, colour and more — these photos are not used on your listing.'
                        )}
                    </p>
                </div>
                <span className="rounded-full bg-[var(--accent-color)]/25 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-[var(--text-color)]">
                    {tr(t, 'form.productform.assist.badge', 'Optional')}
                </span>
            </div>

            <input
                ref={cameraRef}
                type="file"
                accept="image/jpeg,image/png"
                capture="environment"
                className="hidden"
                onChange={(e) => void addFiles(e.target.files)}
                disabled={disabled || loading}
            />
            <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png"
                multiple
                className="hidden"
                onChange={(e) => void addFiles(e.target.files)}
                disabled={disabled || loading}
            />

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <button
                    type="button"
                    disabled={disabled || loading || tagImages.length >= MAX_TAG_IMAGES}
                    onClick={() => cameraRef.current?.click()}
                    className="min-h-11 flex-1 rounded-xl bg-[var(--accent-color)] px-4 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50 transition"
                >
                    {tr(t, 'form.productform.assist.camera', 'Take tag photo')}
                </button>
                <button
                    type="button"
                    disabled={disabled || loading || tagImages.length >= MAX_TAG_IMAGES}
                    onClick={() => fileRef.current?.click()}
                    className="min-h-11 flex-1 rounded-xl border border-[color-mix(in_srgb,var(--accent-color),transparent_35%)] bg-[color-mix(in_srgb,var(--bg-color),white_60%)] px-4 py-2.5 text-sm font-medium text-[var(--text-color)] hover:bg-[color-mix(in_srgb,var(--accent-color),transparent_88%)] disabled:opacity-50 transition"
                >
                    {tr(t, 'form.productform.assist.upload', 'Upload tag photo')}
                </button>
            </div>

            {tagImages.length > 0 && (
                <div className="mt-4">
                    <div className="grid grid-cols-3 gap-2 sm:grid-cols-3">
                        {tagImages.map((img) => (
                            <div key={img.id} className="relative group">
                                <img
                                    src={img.preview}
                                    alt=""
                                    className="h-20 w-full object-cover rounded-lg border border-[color-mix(in_srgb,var(--accent-color),transparent_50%)]"
                                />
                                <button
                                    type="button"
                                    onClick={() => removeTag(img.id)}
                                    className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-6 h-6 min-h-0 rounded-full flex items-center justify-center hover:bg-red-600"
                                    aria-label={tr(t, 'form.productform.assist.remove', 'Remove')}
                                >
                                    ×
                                </button>
                            </div>
                        ))}
                    </div>
                    <p className="mt-2 text-[11px] text-[color-mix(in_srgb,var(--text-color),transparent_45%)]">
                        {tr(
                            t,
                            'form.productform.assist.notListing',
                            'Tag photos are only used to read the label — not shown on your listing.'
                        )}
                    </p>
                </div>
            )}

            <div className="mt-4 flex flex-col sm:flex-row gap-2">
                <button
                    type="button"
                    disabled={disabled || loading || tagImages.length === 0}
                    onClick={() => void handleAnalyze()}
                    className="min-h-11 flex-1 rounded-xl border-2 border-[var(--accent-color)] bg-transparent px-4 py-2.5 text-sm font-semibold text-[var(--text-color)] hover:bg-[var(--accent-color)]/15 disabled:opacity-50 transition"
                >
                    {loading
                        ? tr(t, 'form.productform.assist.reading', 'Reading label…')
                        : tr(t, 'form.productform.assist.analyze', 'Read label')}
                </button>
                {tagImages.length > 0 && (
                    <button
                        type="button"
                        disabled={loading}
                        onClick={clearAll}
                        className="min-h-11 rounded-xl px-4 py-2.5 text-sm text-[color-mix(in_srgb,var(--text-color),transparent_30%)] hover:underline"
                    >
                        {tr(t, 'form.productform.assist.clear', 'Clear tag photos')}
                    </button>
                )}
            </div>

            <ValidationError error={error} />

            {hasSuggestion && (
                <div className="mt-4 rounded-xl border border-[color-mix(in_srgb,var(--accent-color),transparent_45%)] bg-[color-mix(in_srgb,var(--bg-color),white_70%)] p-3 sm:p-4">
                    <p className="text-sm font-medium text-[var(--text-color)] mb-2">
                        {tr(t, 'form.productform.assist.found', 'We found:')}
                    </p>
                    <div className="flex flex-wrap gap-2">
                        {result?.suggestedTitle && (
                            <span className="rounded-full bg-[var(--accent-color)]/20 px-3 py-1 text-xs text-[var(--text-color)]">
                                {tr(t, 'form.productform.assist.chipTitle', 'Title')}: {result.suggestedTitle}
                            </span>
                        )}
                        {Object.entries(result?.fields ?? {}).map(([k, v]) =>
                            v ? (
                                <span
                                    key={k}
                                    className="rounded-full bg-[var(--accent-color)]/20 px-3 py-1 text-xs text-[var(--text-color)]"
                                >
                                    {k}: {v}
                                </span>
                            ) : null
                        )}
                    </div>
                    <button
                        type="button"
                        disabled={disabled || loading || applied}
                        onClick={handleApplyAll}
                        className="mt-3 min-h-11 w-full sm:w-auto rounded-xl bg-[var(--accent-color)] px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50 transition"
                    >
                        {applied
                            ? tr(t, 'form.productform.assist.applied', 'Applied — you can still edit below')
                            : tr(t, 'form.productform.assist.applyAll', 'Apply all')}
                    </button>
                    {applied && (
                        <p className="mt-2 text-xs text-[color-mix(in_srgb,var(--text-color),transparent_40%)]">
                            {tr(
                                t,
                                'form.productform.assist.appliedHint',
                                'Title and matching details were filled where empty. Choose categories to fill specifications too.'
                            )}
                        </p>
                    )}
                </div>
            )}
        </section>
    );
};

export default ListingAssistPanel;
